/**
 * usePermissions Hook
 *
 * Provides permission checking utilities for components.
 * Wraps permissionStore with convenient methods.
 */
import { useCallback } from 'react';
import { usePermissionStore } from '@/store';

/**
 * Hook for permission checking in components
 *
 * @example
 * ```tsx
 * const { hasPermission, canAccess } = usePermissions();
 *
 * // Check specific function permission
 * if (hasPermission('USER')) {
 *   // Show user management features
 * }
 *
 * // Check route access
 * if (canAccess('CUSTOMER')) {
 *   // Allow navigation to customer page
 * }
 * ```
 */
export const usePermissions = () => {
  const isSuperAdmin = usePermissionStore(state => state.isSuperAdmin);
  const hasPermission = usePermissionStore(state => state.hasPermission);
  const hasNodePermission = usePermissionStore(
    state => state.hasNodePermission,
  );
  const canAccessRoute = usePermissionStore(state => state.canAccessRoute);
  const getAllowedFunctions = usePermissionStore(
    state => state.getAllowedFunctions,
  );
  const permissions = usePermissionStore(state => state.permissions);

  /**
   * Check if user has permission for a function
   */
  const checkPermission = useCallback(
    (functionId: string): boolean => {
      return hasPermission(functionId);
    },
    [hasPermission],
  );

  /**
   * Check if user has permission for any of the given functions
   */
  const hasAnyPermission = useCallback(
    (functionIds: string[]): boolean => {
      return functionIds.some(id => hasPermission(id));
    },
    [hasPermission],
  );

  /**
   * Check if user has permission for all of the given functions
   */
  const hasAllPermissions = useCallback(
    (functionIds: string[]): boolean => {
      return functionIds.every(id => hasPermission(id));
    },
    [hasPermission],
  );

  /**
   * Check if user can access a route by functionId
   */
  const canAccess = useCallback(
    (functionId: string): boolean => {
      return canAccessRoute(functionId);
    },
    [canAccessRoute],
  );

  /**
   * Check if user can access a nodeId (for menu items)
   */
  const canAccessNode = useCallback(
    (nodeId: number): boolean => {
      return hasNodePermission(nodeId);
    },
    [hasNodePermission],
  );

  return {
    /** Whether current user is super admin (bypasses all checks) */
    isSuperAdmin,
    /** User's role code */
    roleCode: permissions?.roleCode ?? null,
    /** All allowed function IDs */
    allowedFunctions: getAllowedFunctions(),
    /** All allowed node IDs */
    allowedNodeIds: permissions?.allowedNodeIds ?? [],
    /** Check if user has permission for a function */
    hasPermission: checkPermission,
    /** Check if user has any of the given permissions */
    hasAnyPermission,
    /** Check if user has all of the given permissions */
    hasAllPermissions,
    /** Check if user can access a route */
    canAccess,
    /** Check if user can access a node (menu item) */
    canAccessNode,
  };
};
